import React, { useMemo } from 'react';
import { encodeText, Ecc } from '../utils/qrcodegen';
import { useLanguage } from '../i18n';

/**
 * Renders a scannable QR code as a self-contained inline SVG (no external deps,
 * no network) from the vendored `qrcodegen` encoder. Dark modules are emitted as
 * ONE `<path>` (fast + crisp at any size). Colors adapt to light/dark theme via
 * currentColor-independent explicit fills so the code stays high-contrast.
 *
 * Used by the Telegram QR linking screens (#327), where the value is the `t.me/<bot>?start=<code>`
 * handshake link.
 *
 * The rendered node carries `data-payload` = the exact encoded string. That is
 * the ONLY way a test (or a support session) can verify WHAT a QR encodes: the
 * symbol itself is an opaque module grid, so without it "a QR rendered" and
 * "the right QR rendered" are indistinguishable.
 */
const QRCode = ({ value, size = 200, className = '', title = 'QR code', 'data-testid': testId }) => {
    const { t } = useLanguage();
    const { path, count, error } = useMemo(() => {
        try {
            const qr = encodeText(value || ' ', Ecc.MEDIUM);
            const n = qr.size;
            let d = '';
            for (let y = 0; y < n; y++) {
                for (let x = 0; x < n; x++) {
                    if (qr.getModule(x, y)) d += `M${x} ${y}h1v1h-1z`;
                }
            }
            return { path: d, count: n, error: null };
        } catch (e) {
            return { path: '', count: 0, error: e };
        }
    }, [value]);

    if (error || !count) {
        return (
            <div
                data-testid={testId}
                data-payload={value || ''}
                className={`flex items-center justify-center bg-white text-pai-faint text-xs ${className}`}
                style={{ width: size, height: size }}
            >
                {t('auxQrUnavailable', 'QR unavailable')}
            </div>
        );
    }

    // A 1-module quiet zone keeps the symbol scannable when placed on a colored
    // card. viewBox spans the modules + quiet border; the white rect is the
    // background (QR must be dark-on-light regardless of app theme).
    const quiet = 2;
    const dim = count + quiet * 2;
    return (
        <svg
            data-testid={testId}
            data-payload={value || ''}
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox={`0 0 ${dim} ${dim}`}
            shapeRendering="crispEdges"
            className={className}
            role="img"
            aria-label={title}
        >
            <title>{title}</title>
            <rect width={dim} height={dim} fill="#ffffff" />
            <g transform={`translate(${quiet} ${quiet})`}>
                <path d={path} fill="#111111" />
            </g>
        </svg>
    );
};

export default QRCode;
